import {
  RmaLrpCommodityNames,
  HiqLrpTypeCodeNames,
  RmaLrpTypeCodes,
  RmaLrpCommodityCodes,
  RmaLrpCommodityCodeByName,
  LrpTypeCodeByName,
  getLrpCommodityInfo,
} from '@harvestiq/constants';
import { z } from '@harvestiq/zod';
import { zIsoDate, zDecimalRequired } from '@harvestiq/zod';

export const lrpQuoteRequestSchema = z
  .object({
    commodity: z.nativeEnum(RmaLrpCommodityNames),
    commodityType: z.nativeEnum(HiqLrpTypeCodeNames),
    marketingDate: zIsoDate().optional(),
    salesEffectiveDate: zIsoDate(),
    numberOfHead: z.coerce.number().int().positive().optional(),
    targetWeight: z.coerce.number().positive().optional(),
  })
  .superRefine((val, ctx) => {
    const commodityCode = RmaLrpCommodityCodeByName[val.commodity];
    const typeCode = LrpTypeCodeByName[val.commodityType];
    try {
      getLrpCommodityInfo({ commodityCode, typeCode });
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Commodity Type ${val.commodityType} doesn't match commodity ${val.commodity}`,
      });
    }
  });

export type LrpQuoteRequest = z.infer<typeof lrpQuoteRequestSchema>;

export const lrpQuotesResponseSchema = z.object({
  // FIXME
  data: z.object({
    quotes: z
      .object({
        expectedEndingValue: zDecimalRequired(),
        coverageLevel: zDecimalRequired(),
        coveragePrice: zDecimalRequired(),
        producerPremium: zDecimalRequired(),
        savingsVsCmeTrade: zDecimalRequired(),
      })
      .array(),
    url: z.string().url(),
  }),
});

export type LrpQuotesResponse = z.infer<typeof lrpQuotesResponseSchema>;
// FIXME: It should be consolidated extending lrpQuoteSchema
export const rmaDataItemSchema = z.object({
  expectedEndingValue: zDecimalRequired(),
  coverageLevel: zDecimalRequired(),
  coveragePrice: zDecimalRequired(),
  producerPremium: zDecimalRequired(),
  savingsVsCmeTrade: zDecimalRequired(),
  savingsVsCmeTradeDollar: zDecimalRequired(),
  comparisonPrice: zDecimalRequired(),
  finalSettlement: zDecimalRequired(),
  publicationDate: z.string(),
  barchartFuturesSymbol: z.string(),
  salesEffectiveDate: z.string(),
  endDate: z.string(),
  commodityName: z.string(),
  typeDisplay: z.string(),
  typeCode: z.string(),
  endorsementLengthCount: z.number().int(),
});

export const lrpQuoterResponseSchema = z.object({
  rmaData: z.array(rmaDataItemSchema),
});

export type RmaDataItem = z.infer<typeof rmaDataItemSchema>;
export type LrpQuoterResponse = z.infer<typeof lrpQuoterResponseSchema>;

export const lrpCommodityOptionsRequestSchema = z.object({
  salesEffectiveDate: zIsoDate().optional(),
  distinctOn: z
    .enum(['salesEffectiveDate', 'commodityCode'])
    .array()
    .optional(),
  commodityCodes: z.nativeEnum(RmaLrpCommodityCodes).array().optional(),
  limit: z.number().int().positive().optional(),
});
export type LrpCommodityOptionsRequest = z.infer<
  typeof lrpCommodityOptionsRequestSchema
>;

export const lrpCommodityOptionsSchema = z
  .object({
    commodity: z.nativeEnum(RmaLrpCommodityNames),
    commodityCode: z.nativeEnum(RmaLrpCommodityCodes),
    commodityType: z.nativeEnum(HiqLrpTypeCodeNames),
    typeCode: z.nativeEnum(RmaLrpTypeCodes),
    marketingDates: z.string().array(),
    endorsementLengths: z.number().array(),
    salesEffectiveDate: zIsoDate(),
    targetLowWeight: zDecimalRequired(),
    targetHighWeight: zDecimalRequired(),
    reinsuranceYear: z.number().int(),
  })
  .superRefine((val, ctx) => {
    const commodityCode = RmaLrpCommodityCodeByName[val.commodity];
    const typeCode = LrpTypeCodeByName[val.commodityType];
    try {
      getLrpCommodityInfo({ commodityCode, typeCode });
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Commodity Type ${val.commodityType} doesn't match commodity ${val.commodity}`,
      });
    }
  });
export type LrpCommodityOptions = z.infer<typeof lrpCommodityOptionsSchema>;

export const lrpCommodityOptionsResponseSchema = z.object({
  data: lrpCommodityOptionsSchema.array(),
});

export type LrpCommodityOptionsResponse = z.infer<
  typeof lrpCommodityOptionsResponseSchema
>;
